/**
 * Writing a card into the cells of a live Word table, one cell at a time, word
 * by word under Word's own tracking.
 *
 * A card over several cells reads back from Word as a table fragment, and a
 * package replacing a range that spans cells lands in the first cell as a
 * nested table, so each cell paragraph is written on its own. Word strikes and
 * inserts the engine's own edits for the cell (`TextEditPlanner`), the
 * characters the engine's package marks, by the signed-in Word user, and every
 * earlier tracked change in the cell stays as it was. The engine still builds
 * the cell's package: it is the plan's source of the text the cell must read
 * once written, the preview's, and what a cell holding a field or a hyperlink
 * takes over its content range when Word's search cannot place its words
 * (`writeContentRangePackages`).
 *
 * The paragraphs a card adds in a cell are written after the cells, under
 * Word's tracking, and read back.
 *
 * What the card writes stays written: every write is journaled once its sync answered,
 * and when a later write fails nothing is taken back; the card reports partly applied
 * with the count of writes that landed (`failureKeepingWhatLanded`). The document,
 * never the error, is the record of what landed: a write never takes back what it has
 * written. A cell the writer cannot plan, because Word reads
 * its words differently from the paragraph the card was built over, fails alone: the
 * card's other cells land, and the card reports partly applied: no refusal once the
 * card exists.
 *
 * WHY the engine's package is the last door and not the first
 *   The package round trip was `insertOoxml(replace)` of a cell paragraph's range.
 *   Over the paragraph's whole range, which carries the cell's end mark, Word for Mac
 *   refused it on every cell that ends its row, with GeneralException and nothing
 *   written (live probes, Test text 8 and 33, 2026-10-16); the paragraph's content
 *   range takes the same package on every such cell, in one sync (last-cell probe,
 *   2026-10-16), and that is the range the fallback writes it over. Where Word took it, it was seen to drop the earlier changes' marks with
 *   the package's own, so a cell holding another author's insertion came back as plain
 *   text and the whole-cell rewrite that followed struck all of it as the user's own
 *   change (the "Provider" and "Location" cells); an inserted Korean word lost its East
 *   Asian font, which Word's own tracked write keeps; and `getTrackedChanges()` listed
 *   no deletion a package put at a paragraph's start. On every other input the two doors
 *   left the same text after Accept All and Reject All (side-by-side probe, 2026-10-16),
 *   so the one door that writes every cell is the one kept.
 *
 * WHEN a door fails or misbehaves in a way not yet recorded
 *   CONSEQUENCE
 *     it gets its dated row in `apps/addins/word/.claude/cell-writer-doors-audit.md`
 *     (host, cell shape, Word's error, evidence) before it is fixed; that file holds
 *     every failure each door has shown and the decisions on which door a cell takes.
 */
import { APPLY_FAILURE_INSERT_FAILED } from "@common/constants/appliedActions";
import { OoxmlRange } from "@common/utils/redline/ooxml-range";
import { type PlannedTextEdit, TextEditPlanner } from "@common/utils/redline/text-edit-planner";
import { type WordToken, WordTokenizer } from "@common/utils/redline/text/word-tokenizer";

import { withTemporaryTrackingMode } from "../../document";
import { ApplyFailureError, PartialRedlineApply, type WordErrorDetail, wordErrorDetailOf } from "../../errors";
import { reportError } from "../../sentry-reporter";
import { AcceptedTextWordRanges, type HeldDiffUnit, type WordRangeAcceptedReading, type WordRangeOnAcceptedText } from "../office/accepted-text-word-ranges";
import { WORD_SEPARATORS, queueWordRangesOf } from "../office/word-text-ranges";
import type { WordParagraph } from "../word-document";
import type { AnchoredProposal, BuiltProposal } from "./anchored-proposal";
import type { CardInsertionGroup } from "./card-piece-placement";
import { TRACK_ALL_CHANGES } from "./table-change-writer";

/** Word's `InsertLocation` for writing text over the range the call is made on, in its string form. */
const REPLACE_RANGE = "Replace";
/** Word's `ChangeTrackingVersion` for the text with every tracked change accepted, in its string form. */
const EVERY_CHANGE_ACCEPTED = "Current";
/** Word's `ChangeTrackingMode` that tracks nothing: the engine's package carries its own marks, so Word must add none over it. */
const TRACK_NOTHING = "Off";
/** The most characters Word's `Range.search` takes as the text to find. */
const WORD_SEARCH_TEXT_MAX_CHARS = 255;
/** The character Word reads a manual line break (`w:br`) back as, and writes one for when it is inserted as text. */
const WORD_MANUAL_LINE_BREAK = "\u000b";
/** The character Word's `insertText` starts a new paragraph for. */
const WORD_PARAGRAPH_BREAK = "\n";

const CELL_WORDS_DIFFER_MESSAGE = "Word reads this table cell differently from the paragraph the card was built over, so its words could not be changed in place.";
const CELL_NOT_IN_READ_MESSAGE = "Word did not return this table cell when it was read, so its words could not be changed in place.";
const CELL_WRITE_NOT_IN_DOCUMENT_MESSAGE = "Word did not write this change into the table cell, so nothing this redline wrote was kept.";
const CARD_NOT_WRITTEN_MESSAGE = "Word did not write this redline into the table, so nothing changed. Apply it again, or regenerate it.";

/** The one operation this writer needs of the document's bookmarks: laying a cell's bookmark back over its paragraph once the paragraph was written; true when a bookmark was laid. */
export interface CellBookmarkRelayer {
    layOver(bookmarkNames: string[], paragraphs: WordParagraph[]): Promise<boolean>;
}

/** A cell the card names, as Word holds it: the paragraph its bookmark resolves to, that paragraph's OOXML before any write, and the card built over it. */
interface LiveCell {
    bookmark: string;
    paragraph: Word.Paragraph;
    liveParagraphOoxml: string;
    built: BuiltProposal;
}

/**
 * A cell the card names that the writer refused on its own, nothing of it written:
 * Word's read of the cell holds no paragraph under the card's bookmark, or Word's
 * units do not read as the paragraph the card was built over. The card's other cells
 * still land, and the card reports partly applied with this cell among those planned.
 */
interface RefusedCellWrite {
    bookmark: string;
    refusal: ApplyFailureError;
}

/** The cells a card names as Word holds them, and the ones Word's read gave no paragraph for. */
interface LiveCellsRead {
    liveCells: LiveCell[];
    refused: RefusedCellWrite[];
}

/**
 * The word-by-word plans of the cells whose words Word's ranges or its search place,
 * the cells Word's ranges do not line up for, each refused alone, and the cells
 * holding a field or a hyperlink whose words Word's search could not place, which
 * take the engine's package over their content range.
 */
interface CellWritePlanning {
    plans: CellWritePlan[];
    refused: RefusedCellWrite[];
    packageFallbacks: LiveCell[];
}

/** One cell and the Word ranges that stand on its words, each with its accepted text: "" for a stretch inside a tracked deletion. */
interface CellWordUnits {
    liveCell: LiveCell;
    liveUnits: WordRangeAcceptedReading[];
}

/** The search that places one of the diff's units of a cell, and which of its hits is this unit. */
interface CellUnitSearch {
    unitInText: WordToken;
    searchHits: Word.RangeCollection;
    /** How many times the unit's text occurs in the cell's accepted text: what Word's hits must number to line up with it. */
    expectedHitCount: number;
    /** Which of those occurrences is this unit, counted from 0. */
    occurrenceIndex: number;
}

/** The searches that place every unit of one cell holding a field or a hyperlink, one hit collection per distinct text. */
interface CellUnitSearches {
    liveCell: LiveCell;
    unitSearches: CellUnitSearch[];
}

/**
 * One cell's word-by-word write before Word's ranges are cut: its accepted text,
 * Word's ranges placed on it, and the engine's edits that turn it into the text
 * the card leaves (`TextEditPlanner.editsBetween`).
 */
interface CellEditPlan {
    liveCell: LiveCell;
    acceptedTextBeforeWrite: string;
    placedRanges: WordRangeOnAcceptedText[];
    edits: PlannedTextEdit[];
}

/** One held unit of a range being cut, the search for its text, and which hit is this unit. */
interface HeldUnitSearch {
    heldUnit: HeldDiffUnit;
    searchHits: Word.RangeCollection;
    /** How many times the unit's text occurs in the range's text: what Word's hits must number to line up with it. */
    expectedHitCount: number;
    /** Which of those occurrences is this unit, counted from 0. */
    occurrenceIndex: number;
}

/** The searches that cut one placed range into the diff's units it holds. */
interface PlacedRangeCut {
    placedRange: WordRangeOnAcceptedText;
    heldUnitSearches: HeldUnitSearch[];
}

/**
 * The start or the end of a placed range, where a write into the cell begins or
 * stops, and the offset in the cell's accepted text it stands at.
 */
interface PlacedRangeEdge {
    placedRange: WordRangeOnAcceptedText;
    side: "Start" | "End";
    offset: number;
}

/** The two range edges a write begins and ends at; two edges at one offset are a point, where text is inserted. */
interface PlacedRangeEdges {
    startEdge: PlacedRangeEdge;
    endEdge: PlacedRangeEdge;
}

/** The engine's edits one write covers: from the first edit's start edge to the furthest end edge any of them reaches. */
interface CellEditGroup extends PlacedRangeEdges {
    edits: PlannedTextEdit[];
}

/** One write into a cell under Word's tracking: the characters between the two edges become `writtenText`. */
interface CellRangeWrite extends PlacedRangeEdges {
    writtenText: string;
}

/**
 * Everything the tracked write of one cell needs: the writes over Word's ranges,
 * in cell order, and, for a cell with no word Word reads, the text that goes at
 * the paragraph's start.
 */
interface CellWritePlan {
    liveCell: LiveCell;
    rangeWrites: CellRangeWrite[];
    textForEmptyCell: string;
}

/** A cell paragraph and the Word paragraph object its bookmark is laid back over once the writes are done. */
interface WrittenCellParagraph {
    bookmark: string;
    paragraph: Word.Paragraph;
}

/** How one cell write landed: the cell's words were written under Word's tracking, the engine's package was written over its content, or paragraphs were added after it. */
type LandedCellWriteKind = "words" | "package" | "added_paragraphs";

/**
 * One write the card made that landed, journaled once its sync answered: the cell
 * paragraph it changed, or the paragraph its added paragraphs follow. What landed
 * stays whatever a later write does; the journal is what the card reports as partly
 * applied when one does fail (`failureKeepingWhatLanded`).
 */
interface LandedCellWrite {
    bookmark: string;
    kind: LandedCellWriteKind;
}

/** One planned cell of a failed card as Sentry sees it: its bookmark and whether its write landed. */
interface CellWriteLanding {
    bookmark: string;
    landed: boolean;
}

/** What Sentry receives about a card whose write failed: the card, every planned cell's landing, the added-paragraph groups that landed, and Word's error. */
interface CellWriteFailureReport {
    cardId: string | null;
    cells: CellWriteLanding[];
    addedParagraphGroupsLanded: number;
    addedParagraphGroupsPlanned: number;
    wordError: WordErrorDetail | null;
}

/**
 * What Word holds around the paragraph a group is added after, read just before the
 * group is written: the paragraph, the cell it sits in, and the cell's paragraphs in
 * order.
 */
interface InsertionAnchorRead {
    anchorParagraph: Word.Paragraph;
    enclosingCell: Word.TableCell;
    cellParagraphsBeforeWrite: Word.ParagraphCollection;
}

/** A paragraph written word by word and the words it must read once written, for the read-back of a write Word may drop in silence. */
interface WrittenTextCheck {
    paragraph: Word.Paragraph;
    expectedText: string;
}

export class CellChangeWriter {
    /**
     * Writes every cell the card names and changes, word by word under Word's tracking,
     * then lays each cell's bookmark back over its paragraph and selects the first
     * written cell.
     *
     * CASES
     *   - a cell the card leaves as it is               -> never read: it needs no read to land, so it is
     *                                                      neither refused nor counted among the writes
     *                                                      (`readLiveCells`)
     *   - a cell the card names is gone                 -> skipped; the other cells are still written
     *   - a cell already reads as proposed              -> left alone, no revision written
     *   - Word's read of a cell holds no paragraph under
     *     the card's bookmark, or Word's units do not
     *     read as the paragraph the card was built over -> that cell is refused alone, nothing of it
     *                                                      written; the other cells land, and the card
     *                                                      reports partly applied (`failureKeepingWhatLanded`)
     *   - a changed cell holding a field or a hyperlink -> its words found by Word's search in the text
     *                                                      the document shows, then as any other cell;
     *                                                      when the search cannot place them, the engine's
     *                                                      package over the cell's content range
     *                                                      (`planWordWrites`, `writeContentRangePackages`)
     *   - any other changed cell                        -> its words struck and inserted under Word's
     *                                                      tracking, all cells in one sync, each read back
     *   - the card adds paragraphs to a cell            -> written after the cells, group by group, each group
     *                                                      read back (`writeInsertionGroup`)
     *   - a write fails or reads back missing           -> every write that landed stays; the card reports partly
     *                                                      applied with the count that landed (`failureKeepingWhatLanded`)
     *
     * WHY the bookmark is laid again
     *   A word-level write that covers the whole bookmarked text takes the bookmark
     *   with it. The card, the drawer and the landing audit all find the cell by that
     *   bookmark, so without it a written cell reads as lost and the card as failed.
     *
     * WHY a refused cell never refuses the card
     *   The card exists once the user applied it, and nothing it wrote is taken back:
     *   a cell Word reads differently costs that cell its write,
     *   reported as partly applied, never the other cells theirs.
     */
    static async write(
        context: Word.RequestContext,
        proposal: AnchoredProposal,
        stampedAt: string,
        bookmarks: CellBookmarkRelayer,
        /** What names the card in a failure report; null when the caller has no id for it. */
        cardId: string | null,
    ): Promise<void> {
        const cellsRead = await CellChangeWriter.readLiveCells(context, proposal, stampedAt);
        const changedCells = cellsRead.liveCells.filter((liveCell) => liveCell.built.outcome.changeCount > 0);
        /** The card's added paragraphs by the paragraph each group follows: the one placement the preview draws by too (`cardInsertionGroupsOf`). */
        const insertionGroups = proposal.cellInsertionGroups();
        /**
         * WHEN no cell changes, no cell was refused and the card adds no paragraph
         *   CONSEQUENCE
         *     nothing is written.
         */
        if (changedCells.length === 0 && cellsRead.refused.length === 0 && insertionGroups.length === 0) return;
        /** The cells the card planned a write for, refused ones included: what the partly-applied count is out of. */
        const plannedCellBookmarks = [...changedCells.map((liveCell) => liveCell.bookmark), ...cellsRead.refused.map((refusedCell) => refusedCell.bookmark)];
        const landedWrites: LandedCellWrite[] = [];
        /** The cell paragraphs written so far, whose bookmarks are laid back once the cells are written, or on a failure before that. */
        const writtenParagraphs: WrittenCellParagraph[] = [];
        /** Every cell refused on its own, by the read or by the plan, reported once the other writes are done. */
        const refusedCells: RefusedCellWrite[] = [...cellsRead.refused];
        let bookmarksLaidBack = false;
        try {
            const planning = await CellChangeWriter.planWordWrites(context, changedCells);
            refusedCells.push(...planning.refused);
            await CellChangeWriter.writePlannedWords(context, planning.plans, landedWrites);
            await CellChangeWriter.writeContentRangePackages(context, planning.packageFallbacks, landedWrites);
            const writtenCells = [...planning.plans.map((plan) => plan.liveCell), ...planning.packageFallbacks];
            writtenParagraphs.push(...writtenCells.map((liveCell) => ({ bookmark: liveCell.bookmark, paragraph: liveCell.paragraph })));
            await CellChangeWriter.verifyWordWrittenTexts(context, writtenCells
                .filter((liveCell) => !new OoxmlRange(liveCell.liveParagraphOoxml).hasRevisionMarks())
                .map((liveCell) => ({ paragraph: liveCell.paragraph, expectedText: CellChangeWriter.acceptedTextAfterWriteOf(liveCell) })));
            /**
             * WHY the bookmarks are laid back before any paragraph is added
             *   A paragraph is added after the one its `afterBookmark` names, which may be a
             *   cell this card just wrote, whose bookmark the write can take with it. Laid
             *   back first, every bookmark resolves to its paragraph, and each is laid by the
             *   paragraph's id before Word splits a paragraph to add one after it.
             */
            await CellChangeWriter.layBookmarksBack(context, writtenParagraphs, bookmarks);
            bookmarksLaidBack = true;
            for (const insertionGroup of insertionGroups) await CellChangeWriter.writeInsertionGroup(context, insertionGroup, landedWrites, bookmarks, cardId);
        } catch (writeFailure) {
            /**
             * WHEN a write failed before the written cells had their bookmarks laid back
             *   CONSEQUENCE
             *     they are laid back now: what landed stays, and the card, the drawer and the
             *     landing audit must still find it by its bookmark.
             */
            if (!bookmarksLaidBack) await CellChangeWriter.layBookmarksBackQuietly(context, writtenParagraphs, bookmarks, cardId);
            throw CellChangeWriter.failureKeepingWhatLanded(cardId, plannedCellBookmarks, insertionGroups, landedWrites, writeFailure);
        }
        /**
         * WHEN a cell was refused alone while the other writes landed
         *   CONSEQUENCE
         *     the card reports partly applied with what landed, out of every cell and group it
         *     planned; nothing written is taken back. With nothing landed, the refusal itself is
         *     the card's failure.
         */
        if (refusedCells.length > 0) throw CellChangeWriter.refusalKeepingWhatLanded(plannedCellBookmarks, insertionGroups, landedWrites, refusedCells[0].refusal);
        /**
         * WHEN a cell was written
         *   CONSEQUENCE
         *     its bookmark is selected, the way a paragraph write selects its range, so the
         *     reader sees where the change landed.
         * WHEN only paragraphs were added
         *   CONSEQUENCE
         *     the paragraph the first one follows is selected.
         */
        const selectedBookmark = changedCells[0]?.bookmark ?? insertionGroups[0].afterBookmark;
        context.document.getBookmarkRangeOrNullObject(selectedBookmark).select();
        await context.sync();
    }

    /**
     * The text a cell reads once every change is accepted after the card's write: its package's accepted text.
     *
     * FALLBACK
     *   "" for a package with no paragraph: the cell is to read empty.
     */
    private static acceptedTextAfterWriteOf(liveCell: LiveCell): string {
        /** The package's first and only paragraph; "" for a package with none, which leaves the cell empty. */
        return new OoxmlRange(liveCell.built.outcome.packageOoxml).allChangesAcceptedTexts()[0] ?? "";
    }

    /**
     * Adds one group's paragraphs after the paragraph its bookmark names, under Word's
     * tracking, and reads the cell back: three syncs, one to read the cell, one to
     * write, one to read it again.
     *
     * WHY the cell is read back
     *   Word for Mac answers `insertParagraph(…, "After")` on a cell's last paragraph
     *   with a paragraph that is in no document and raises no error (live probe,
     *   2026-10-15). The cell's paragraphs, read again, are the only proof that each
     *   added paragraph landed where the card puts it.
     *
     * WHY one group at a time
     *   Each read of the cell then holds every group written before it, so two groups
     *   in one cell are each checked against the cell as the other left it.
     *
     * EDGE CASE
     *   A new paragraph takes the formatting of the paragraph it follows, list
     *   membership included, whatever the card's `list`: a cell paragraph is rarely a
     *   list item, and Word's list calls fail under tracking.
     */
    private static async writeInsertionGroup(
        context: Word.RequestContext,
        insertionGroup: CardInsertionGroup,
        landedWrites: LandedCellWrite[],
        bookmarks: CellBookmarkRelayer,
        cardId: string | null,
    ): Promise<void> {
        const anchorRead = CellChangeWriter.queueInsertionAnchorRead(context, insertionGroup.afterBookmark);
        await context.sync();
        const cellParagraphsBeforeWrite = anchorRead.cellParagraphsBeforeWrite.items;
        const anchorIndexInCell = cellParagraphsBeforeWrite.findIndex((cellParagraph) => cellParagraph.uniqueLocalId === anchorRead.anchorParagraph.uniqueLocalId);
        /**
         * EXAMPLE
         *   7 paragraphs in the cell -> the last is index 7 - 1 = 6
         */
        const anchorEndsItsCell = anchorIndexInCell === cellParagraphsBeforeWrite.length - 1;
        /**
         * The group's lines among a read of the cell: the paragraphs straight after the
         * anchor, one per line.
         *
         * EXAMPLE
         *   anchor index 0, two lines -> paragraphs 0 + 1 = 1 up to 1 + 2 = 3 (exclusive)
         */
        const groupLinesIn = (cellParagraphs: Word.Paragraph[]): Word.Paragraph[] =>
            cellParagraphs.slice(anchorIndexInCell + 1, anchorIndexInCell + 1 + insertionGroup.insertedTexts.length);
        /**
         * WHEN the cell already holds the group's paragraphs straight after the anchor
         *   CONSEQUENCE
         *     nothing is written for the group: the card added them before, and the landing
         *     audit reads them there as applied (`groupAlreadyFollowsAnchor`). Their ids are
         *     laid on them, so a line added before it carried one is found by it too.
         */
        if (await CellChangeWriter.groupAlreadyFollowsAnchor(context, cellParagraphsBeforeWrite, anchorIndexInCell, insertionGroup.insertedTexts)) {
            await CellChangeWriter.layInsertedParagraphIds(context, groupLinesIn(cellParagraphsBeforeWrite), insertionGroup, bookmarks, cardId);
            return;
        }
        await withTemporaryTrackingMode(context, TRACK_ALL_CHANGES, async () => {
            CellChangeWriter.queueParagraphsAfter(anchorRead.anchorParagraph, anchorEndsItsCell, insertionGroup.insertedTexts);
            await context.sync();
        });
        /**
         * EXAMPLE
         *   cell ["COMPANY:", "By: "], anchor index 1, added ["Name: Morgan Chen"]
         *   -> expected ["COMPANY:", "By: ", "Name: Morgan Chen"]: the cell up to and with the anchor (0..2),
         *      the added paragraphs, then the cell after the anchor (2..)
         */
        const cellTextsExpected = [
            ...cellParagraphsBeforeWrite.slice(0, anchorIndexInCell + 1).map((cellParagraph) => cellParagraph.text),
            ...insertionGroup.insertedTexts,
            ...cellParagraphsBeforeWrite.slice(anchorIndexInCell + 1).map((cellParagraph) => cellParagraph.text),
        ];
        const cellParagraphsAfterWrite = anchorRead.enclosingCell.body.paragraphs;
        cellParagraphsAfterWrite.load("items/uniqueLocalId,items/text");
        await context.sync();
        const cellReadsAsExpected = cellParagraphsAfterWrite.items.length === cellTextsExpected.length
            && cellParagraphsAfterWrite.items.every((cellParagraph, paragraphIndex) => TextEditPlanner.requestWritesNothing(cellParagraph.text, cellTextsExpected[paragraphIndex]));
        /**
         * WHEN the cell does not read as the cell before the write with the group's paragraphs after the anchor
         *   CONSEQUENCE
         *     insert_failed: Word wrote a paragraph somewhere else, or nowhere; the group is not journaled, and
         *     the card reports partly applied with what landed before it
         */
        if (!cellReadsAsExpected) throw new ApplyFailureError(CELL_WRITE_NOT_IN_DOCUMENT_MESSAGE, APPLY_FAILURE_INSERT_FAILED);
        landedWrites.push({ bookmark: insertionGroup.afterBookmark, kind: "added_paragraphs" });
        await CellChangeWriter.layInsertedParagraphIds(context, groupLinesIn(cellParagraphsAfterWrite.items), insertionGroup, bookmarks, cardId);
    }

    /**
     * Lays on each line of a group the id the card proposed it with
     * (`CardInsertionGroup.insertedParagraphIds`), through the same relayer the writer
     * lays a written cell's bookmark back with, so every reader that names the id (a
     * comment the card carries on the line, a review comment on a remedy's new line,
     * Go to issue) finds the line the writer added. `groupLines` come from a read of the
     * cell that loaded each paragraph's id and text.
     *
     * FALLBACK
     *   A lay Word refuses goes to Sentry and the card still reads as written: the lines
     *   landed and were read back, and a write that landed is never failed for a bookmark.
     *   Each line is laid on its own, so a refusal on one line never leaves the lines
     *   after it without their ids.
     *
     * NULL
     *   A null id is a line proposed before new paragraphs carried one: nothing is laid on it.
     *
     * EXAMPLE
     *   group ["Acme Widgets, PLLC", "a Delaware company"], ids [_atomic_record_gateway, _plus_border_sum]
     *   -> layOver([_atomic_record_gateway], [line 1]), layOver([_plus_border_sum], [line 2])
     */
    private static async layInsertedParagraphIds(
        context: Word.RequestContext,
        groupLines: Word.Paragraph[],
        insertionGroup: CardInsertionGroup,
        bookmarks: CellBookmarkRelayer,
        cardId: string | null,
    ): Promise<void> {
        const linesToName = insertionGroup.insertedParagraphIds.flatMap((insertedParagraphId, lineIndex) => {
            const groupLine = groupLines[lineIndex];
            /**
             * CASES
             *   - the line carries no id, or the read holds no paragraph for it -> nothing to lay
             *   - otherwise                                                     -> its id, on that paragraph
             */
            return insertedParagraphId === null || groupLine === undefined ? [] : [{ insertedParagraphId, groupLine }];
        });
        for (const { insertedParagraphId, groupLine } of linesToName) {
            try {
                await bookmarks.layOver([insertedParagraphId], [{ uniqueLocalId: groupLine.uniqueLocalId, text: groupLine.text }]);
            } catch (layFailure) {
                reportError(layFailure, {
                    tags: { where: "cell-change-writer.lay-inserted-paragraph-ids" },
                    extra: { cardId, paragraphId: insertedParagraphId, wordError: wordErrorDetailOf(layFailure) },
                });
            }
        }
    }

    /**
     * Whether the cell already holds a group's paragraphs straight after the anchor, in
     * the card's order, each reading as the card adds it once every change is accepted.
     *
     * WHY
     *   A cell card applied a second time (shown pending again, or in a second Apply All)
     *   is built over its cell paragraphs and counts each added paragraph as a change,
     *   whatever already follows the anchor, so the writer added the lines again and the
     *   cell read them twice (R28, found by -eb, 2026-10-16).
     *
     * WHY the text with every change accepted
     *   `paragraph.text` still reads a line the user struck. A line struck since the card
     *   added it is gone once the change is accepted, so the card adds it again.
     *
     * WHY the plain text first
     *   It came with the cell's read at no cost and tells almost every group apart; only a
     *   group whose lines read the same there costs the one more sync of its accepted texts.
     *
     * EXAMPLE
     *   cell ["COMPANY:", "Acme Widgets, PLLC", "a Delaware company", "By: "], anchor index 0,
     *   group ["Acme Widgets, PLLC", "a Delaware company"]
     *   -> the paragraphs at 0 + 1 = 1 up to 1 + 2 = 3 read as the group -> true
     *
     * EDGE CASE
     *   An anchor not found among the cell's paragraphs (index -1) is never already
     *   followed: the write goes ahead, and its read-back refuses it.
     */
    private static async groupAlreadyFollowsAnchor(context: Word.RequestContext, cellParagraphs: Word.Paragraph[], anchorIndexInCell: number, insertedTexts: string[]): Promise<boolean> {
        if (anchorIndexInCell < 0) return false;
        /** The paragraphs from the one after the anchor, as many as the group adds. */
        const paragraphsAfterAnchor = cellParagraphs.slice(anchorIndexInCell + 1, anchorIndexInCell + 1 + insertedTexts.length);
        /** Spacing aside, as the read-back compares a written cell (`TextEditPlanner.requestWritesNothing`). */
        const readAsTheGroup = (readTexts: string[]): boolean =>
            readTexts.length === insertedTexts.length && readTexts.every((readText, lineIndex) => TextEditPlanner.requestWritesNothing(readText, insertedTexts[lineIndex]));
        if (!readAsTheGroup(paragraphsAfterAnchor.map((cellParagraph) => cellParagraph.text))) return false;
        const acceptedTexts = paragraphsAfterAnchor.map((cellParagraph) => cellParagraph.getRange().getReviewedText(EVERY_CHANGE_ACCEPTED));
        await context.sync();
        return readAsTheGroup(acceptedTexts.map((acceptedText) => acceptedText.value));
    }

    /**
     * Queues the read of everything a group's write and its check need: the paragraph
     * the bookmark names, its cell, and the cell's paragraphs with their ids and texts.
     *
     * WHY `parentTableCell` and not its null-object form
     *   This writer adds paragraphs inside a cell only; a bookmark outside every table
     *   fails the sync, and the card reports what landed before this group.
     *
     * WHY two office-addins lint rules are off on the `parentTableCell` read
     *   `parentTableCell` is a navigation property: a proxy Word resolves at the sync,
     *   read with no load. The stock plugin (eslint-plugin-office-addins) reports every
     *   property read on an object a `getFirst()` made unless that property was loaded,
     *   whatever the order of the lines, and a load of a navigation property is the
     *   slow call its own `no-navigational-load` rule warns on. The add-in turns the
     *   same rules off in place elsewhere (`document.ts`, `comment-survival-probe.ts`).
     */
    private static queueInsertionAnchorRead(context: Word.RequestContext, afterBookmark: string): InsertionAnchorRead {
        const anchorParagraph = context.document.getBookmarkRange(afterBookmark).paragraphs.getFirst();
        anchorParagraph.load("uniqueLocalId");
        // eslint-disable-next-line office-addins/load-object-before-read, office-addins/call-sync-before-read -- a navigation property is read with no load and no sync
        const enclosingCell = anchorParagraph.parentTableCell;
        const cellParagraphsBeforeWrite = enclosingCell.body.paragraphs;
        cellParagraphsBeforeWrite.load("items/uniqueLocalId,items/text");
        return { anchorParagraph, enclosingCell, cellParagraphsBeforeWrite };
    }

    /**
     * Queues the write of a group's paragraphs after the anchor paragraph, by the call
     * Word for Mac was seen to honour at that place (live probe, 2026-10-15).
     *
     * CASES
     *   - the anchor ends its cell -> one `insertText` at the anchor's end, each paragraph
     *                                 led by a paragraph break: `insertParagraph(…, "After")`
     *                                 there returns a paragraph that is in no document, and
     *                                 `body.insertParagraph(…, "End")` writes the text into the
     *                                 anchor itself
     *   - any other anchor         -> `insertParagraph(…, "After")` on the anchor, last
     *                                 paragraph first, so each lands in front of the one
     *                                 written before it and the group reads in card order
     *
     * EXAMPLE
     *   anchor "By: " ends its cell, ["Name: Morgan Chen", "Title: Director"]
     *   -> insertText("\nName: Morgan Chen\nTitle: Director", "End")
     */
    private static queueParagraphsAfter(anchorParagraph: Word.Paragraph, anchorEndsItsCell: boolean, insertedTexts: string[]): void {
        const insertedWordTexts = insertedTexts.map((insertedText) => CellChangeWriter.asWordText(insertedText));
        /** WHEN the anchor ends its cell CONSEQUENCE one insertion, every paragraph break and line in it. */
        if (anchorEndsItsCell) {
            anchorParagraph.insertText(insertedWordTexts.map((insertedWordText) => `${WORD_PARAGRAPH_BREAK}${insertedWordText}`).join(""), "End");
            return;
        }
        for (const insertedWordText of [...insertedWordTexts].reverse()) anchorParagraph.insertParagraph(insertedWordText, "After");
    }

    /**
     * Reads back the paragraphs Word was asked to write words into where it has been
     * seen to write nothing and say nothing, and refuses the card when one does not
     * read as the card wrote it.
     *
     * WHY
     *   A signature cell's blank last line took no package and raised no error, so the
     *   card's other cells landed and the card said applied. Read back here, a write that
     *   did not land is reported, and the card says partly applied.
     *
     * WHY the words and not the characters
     *   Word reads a written line break and a non-breaking space in its own characters,
     *   and ends a cell paragraph's reviewed text with the cell's mark as a tab ("By:\t",
     *   live replay, 2026-10-16); the engine's test for "writes nothing" sets spacing
     *   aside the same way.
     *
     * WHY the text with every change accepted, and not `paragraph.text`
     *   `paragraph.text` reads struck words as if they stood: a cell whose words the card
     *   struck still read "The finance lead approved it.", and its write would read as
     *   missing.
     */
    private static async verifyWordWrittenTexts(context: Word.RequestContext, writtenTextChecks: WrittenTextCheck[]): Promise<void> {
        /** WHEN no paragraph Word may drop a write into was written CONSEQUENCE nothing is read back. */
        if (writtenTextChecks.length === 0) return;
        const acceptedTexts = await CellChangeWriter.acceptedTextsNowOf(context, writtenTextChecks.map(({ paragraph }) => paragraph));
        const missingWrite = writtenTextChecks.find(({ expectedText }, checkIndex) => !TextEditPlanner.requestWritesNothing(acceptedTexts[checkIndex], expectedText));
        /**
         * WHEN a paragraph does not read as the card wrote it
         *   CONSEQUENCE
         *     insert_failed: Word wrote nothing there and said nothing; the card reports partly applied
         *     with the writes that did land
         */
        if (missingWrite !== undefined) throw new ApplyFailureError(CELL_WRITE_NOT_IN_DOCUMENT_MESSAGE, APPLY_FAILURE_INSERT_FAILED);
    }

    /**
     * Each paragraph's text with every tracked change accepted, read from the paragraph's
     * own OOXML in one sync.
     *
     * WHY the OOXML and not `getReviewedText`
     *   Word for Mac answers `getReviewedText("Current")` on a paragraph whose words a
     *   tracked `insertText(…, "Replace")` struck with the struck words still in it
     *   ("tenfive (5) Business Days" for "five" -> "ten"; live probe, 2026-10-16), while the
     *   paragraph's OOXML carries the `w:del`. The package door's read-back trusts the
     *   same OOXML.
     *
     * WHY the content range
     *   A cell paragraph's whole range reads back as its whole row; the content range
     *   holds the paragraph alone.
     *
     * FALLBACK
     *   Word refuses the content read of some cell-ending paragraphs (a signature cell's
     *   blank last line, 2026-10-15): every paragraph is then read by `getReviewedText`,
     *   which reads a line that only gained words right.
     */
    private static async acceptedTextsNowOf(context: Word.RequestContext, paragraphs: Word.Paragraph[]): Promise<string[]> {
        const contentReads = paragraphs.map((paragraph) => paragraph.getRange("Content").getOoxml());
        try {
            await context.sync();
            /** The read's only paragraph; "" for a read with none, which is an empty line. */
            return contentReads.map((contentRead) => new OoxmlRange(contentRead.value).allChangesAcceptedTexts()[0] ?? "");
        } catch {
            const reviewedTexts = paragraphs.map((paragraph) => paragraph.getRange().getReviewedText(EVERY_CHANGE_ACCEPTED));
            await context.sync();
            return reviewedTexts.map((reviewedText) => reviewedText.value);
        }
    }

    /**
     * The error the apply fails with once a step failed. Nothing the card wrote is taken
     * back: every write that landed stays in the document.
     *
     * WHY nothing is taken back
     *   Word has raised GeneralException on a sync whose write landed (a row's last
     *   cell, Word for Mac, 2026-10-16): an undo on that error removed two correct
     *   cells for a third that was also correct, and the user watched the card land and
     *   vanish. The document is the record of what landed, never the error.
     *
     * CASES
     *   - a write landed before the failure                       -> partly applied: the writes that
     *                                                                landed, out of those planned
     *   - a write's read-back refused it and nothing landed
     *     (ApplyFailureError)                                     -> that refusal, as it is
     *   - Word failed before any write landed                     -> insert_failed with Word's error as
     *                                                                its cause
     *
     * WHY Sentry hears of every failure here but the read-back refusal with nothing landed
     *   Every failure that reaches this catch came from a write Word was asked for: Word's
     *   own error, a read-back that disagrees with what Word was asked to write, or an
     *   error of this writer's. A cell the writer refused before asking for any write is
     *   no failure of Word's and comes through `refusalKeepingWhatLanded` instead.
     *
     * EXAMPLE
     *   three cells planned, two journaled, the third's sync threw
     *   -> "Partly applied — 2 of 3 changes reached the document."
     */
    private static failureKeepingWhatLanded(
        cardId: string | null,
        plannedCellBookmarks: string[],
        insertionGroups: CardInsertionGroup[],
        landedWrites: LandedCellWrite[],
        writeFailure: unknown,
    ): Error {
        const failureCause = writeFailure instanceof Error ? writeFailure : new Error(String(writeFailure));
        const plannedWriteCount = CellChangeWriter.plannedWriteCountOf(plannedCellBookmarks, insertionGroups);
        /** WHEN the writer refused a cell and nothing had landed CONSEQUENCE that refusal, as it is. */
        if (writeFailure instanceof ApplyFailureError && landedWrites.length === 0) return writeFailure;
        reportError(failureCause, {
            tags: { where: "cell-change-writer.write" },
            extra: { ...CellChangeWriter.failureReportOf(cardId, plannedCellBookmarks, insertionGroups, landedWrites, failureCause) },
        });
        /** WHEN a write landed CONSEQUENCE partly applied: what landed stays, counted out of what was planned. */
        if (landedWrites.length > 0) return new PartialRedlineApply(landedWrites.length, plannedWriteCount, failureCause);
        return new ApplyFailureError(CARD_NOT_WRITTEN_MESSAGE, APPLY_FAILURE_INSERT_FAILED, failureCause);
    }

    /**
     * The card's failure when the writer refused a cell on its own reading of it, after
     * the other writes were made.
     *
     * CASES
     *   - a write landed    -> partly applied: the writes that landed, out of those planned,
     *                          with the refusal as the cause the card shows
     *   - nothing landed    -> the refusal, as it is
     *
     * WHY nothing goes to Sentry
     *   A refusal is this writer's reading of the cell before any write was asked for:
     *   Word's read holds no paragraph under the card's bookmark, or Word's units do not
     *   read as the paragraph the card was built over. It is no error of Word's, and the
     *   card carries it as its reason (reviewer at 42f9e48bd).
     */
    private static refusalKeepingWhatLanded(plannedCellBookmarks: string[], insertionGroups: CardInsertionGroup[], landedWrites: LandedCellWrite[], refusal: ApplyFailureError): Error {
        if (landedWrites.length === 0) return refusal;
        return new PartialRedlineApply(landedWrites.length, CellChangeWriter.plannedWriteCountOf(plannedCellBookmarks, insertionGroups), refusal);
    }

    /**
     * What the partly-applied count is out of: one write per planned cell and one per
     * group of added paragraphs.
     *
     * EXAMPLE
     *   cells _item, _fee and one group of added lines -> 2 + 1 = 3
     */
    private static plannedWriteCountOf(plannedCellBookmarks: string[], insertionGroups: CardInsertionGroup[]): number {
        return plannedCellBookmarks.length + insertionGroups.length;
    }

    /**
     * What Sentry receives about a card whose write failed: the card, each planned cell
     * with whether its write landed, the added-paragraph groups that landed out of those
     * planned, and Word's error fields.
     *
     * EXAMPLE
     *   cells   _item (landed), _basis (landed), _fee (refused)
     *   -> cells [{ bookmark "_item", landed true }, { "_basis", true }, { "_fee", false }]
     */
    private static failureReportOf(
        cardId: string | null,
        plannedCellBookmarks: string[],
        insertionGroups: CardInsertionGroup[],
        landedWrites: LandedCellWrite[],
        failureCause: Error,
    ): CellWriteFailureReport {
        /** The cells whose words or package landed, by bookmark; a group of added paragraphs is journaled under its anchor's bookmark and is no cell. */
        const landedCellBookmarks = new Set(landedWrites.filter((landedWrite) => landedWrite.kind !== "added_paragraphs").map((landedWrite) => landedWrite.bookmark));
        return {
            cardId,
            cells: plannedCellBookmarks.map((bookmark) => ({ bookmark, landed: landedCellBookmarks.has(bookmark) })),
            addedParagraphGroupsLanded: landedWrites.filter((landedWrite) => landedWrite.kind === "added_paragraphs").length,
            addedParagraphGroupsPlanned: insertionGroups.length,
            wordError: wordErrorDetailOf(failureCause),
        };
    }

    /**
     * Resolves every cell whose words the card changes to its paragraph in Word, reads
     * the paragraph's OOXML and builds the card over each: two syncs for all cells.
     *
     * WHY only the cells the card changes
     *   A card lists every cell of the rows it covers, changed or not. A cell the card
     *   leaves as it is needs no read to land, so a read of it that holds no paragraph
     *   under the card's bookmark refused nothing the card asked for; counted among the
     *   planned writes, it reported a card whose every change was in the document as
     *   partly applied, deselected and locked (reviewer at 42f9e48bd).
     *
     * RETURNS
     *   The changed cells whose bookmark still resolves to a paragraph, in the card's
     *   order, each with its own paragraph alone as `liveParagraphOoxml`; and, refused,
     *   the changed cells whose read holds no paragraph under the card's bookmark
     *   (`cellParagraphOf`), which the card reports as not landed.
     */
    private static async readLiveCells(context: Word.RequestContext, proposal: AnchoredProposal, stampedAt: string): Promise<LiveCellsRead> {
        const candidates = proposal.cellProposals().filter((cellProposal) => cellProposal.proposal.changesParagraphText()).map((cellProposal) => {
            const paragraph = context.document.getBookmarkRangeOrNullObject(cellProposal.bookmark).paragraphs.getFirstOrNullObject();
            paragraph.load("isNullObject");
            return { cellProposal, paragraph };
        });
        await context.sync();
        const foundParagraphs = candidates.filter((candidate) => !candidate.paragraph.isNullObject);
        /**
         * WHY the paragraph is read through its whole range
         *   Word refuses `Paragraph.getOoxml()` with GeneralException on an empty
         *   paragraph that ends its cell, and all the card's cells are read in this one
         *   sync, so that one refusal failed every card that names such a paragraph
         *   (a signature block's blank last line, 2026-10-15). `getRange().getOoxml()`
         *   reads every cell paragraph, empty ones included (live probe, same day).
         */
        const foundCells = foundParagraphs.map((candidate) => ({ ...candidate, liveOoxml: candidate.paragraph.getRange().getOoxml() }));
        await context.sync();
        const cellsRead: LiveCellsRead = { liveCells: [], refused: [] };
        for (const foundCell of foundCells) {
            const liveParagraphOoxml = CellChangeWriter.cellParagraphOf(foundCell.liveOoxml.value, foundCell.cellProposal.bookmark);
            /**
             * WHEN no paragraph of the read answers to the bookmark
             *   CONSEQUENCE
             *     the cell is refused alone, insert_failed: nothing is written over a cell the card
             *     does not name, and the card's other cells still land
             */
            if (liveParagraphOoxml === null) {
                cellsRead.refused.push({ bookmark: foundCell.cellProposal.bookmark, refusal: new ApplyFailureError(CELL_NOT_IN_READ_MESSAGE, APPLY_FAILURE_INSERT_FAILED) });
                continue;
            }
            cellsRead.liveCells.push({
                bookmark: foundCell.cellProposal.bookmark,
                paragraph: foundCell.paragraph,
                liveParagraphOoxml,
                built: foundCell.cellProposal.proposal.buildOverLiveParagraph(liveParagraphOoxml, stampedAt),
            });
        }
        return cellsRead;
    }

    /**
     * The cell paragraph a read of the paragraph's range was made for, alone in a package.
     *
     * WHY
     *   Word can answer the read of a cell paragraph with the whole row, every cell of
     *   it. Taking the row's first paragraph built the card over the first column and
     *   wrote that column's words into the cell the card names, struck, in place of
     *   the cell's own words (the Fee cell came to read "Platform subscription" struck
     *   then "$125,000", its "$120,000" gone). The card's bookmark sits in its own cell.
     *
     * EXAMPLE
     *   row read   Platform subscription | per year | $120,000   (bookmark _fee in the third cell)
     *   returns    the "$120,000" paragraph
     *
     * CASES, for the shapes Word was seen to read (live probes, 2026-10-15)
     *   - Word for Mac, a cell's last paragraph       -> the row: both cells, the bookmark in its own cell
     *   - Word for Mac, any other cell paragraph      -> the paragraph, then the package's closing paragraph
     *   - Word Online, an empty cell                  -> the paragraph alone
     *
     * CASES, for what is returned
     *   - a paragraph in the read answers to the bookmark -> that paragraph, alone in a package
     *   - the read holds no table cell and no paragraph
     *     answers to the bookmark (an empty paragraph,
     *     whose zero-width bookmark Word leaves out)      -> the bookmark is put back on the read's first
     *                                                        paragraph first, then as above
     *   - none does                                       -> null: the caller refuses that cell alone
     *                                                        (`readLiveCells`), rather than write over a
     *                                                        cell the card does not name
     *
     * WHY a read without a table cell may take the bookmark back
     *   The read was made of the one paragraph that bookmark resolves to, so when Word
     *   gives no row around it, its first paragraph is that paragraph; whatever follows is
     *   the closing paragraph Word ends every package with. A row read holds every cell's
     *   paragraph and gives no such certainty, so it is left as Word gave it.
     *
     * EXAMPLE
     *   Word Online read of the empty Annual cell   <w:p w:rsidR="00ED02A7"/>
     *   bookmark                                    _leg_letter_wire
     *   -> <w:p w:rsidR="00ED02A7"><w:bookmarkStart w:name="_leg_letter_wire"/><w:bookmarkEnd/></w:p>
     *
     *   Word for Mac read of the empty mid-cell line  <w:p/><w:p><w:pPr><w:sectPr/></w:pPr></w:p>
     *   bookmark                                      _begin_degree_blue
     *   -> the bookmark is laid on the first <w:p/>
     */
    private static cellParagraphOf(cellReadOoxml: string, bookmark: string): string | null {
        return OoxmlRange.paragraphReadOfBookmark(cellReadOoxml, bookmark);
    }

    /**
     * Plans the write of each cell, for all cells together; nothing is written here.
     * A cell Word cuts into ranges takes one sync to cut its paragraph into Word's
     * ranges, one to read each range's accepted text, and a third only when an edit
     * begins or ends inside a range that holds more than the diff's one unit (a
     * Japanese, Chinese or Thai sentence, two words Word did not cut apart at a tab,
     * a word with the non-breaking space Word left on it): the searches that cut
     * those ranges, every one queued before that one sync. A cell holding a field or
     * a hyperlink is placed by searches instead (`queueUnitSearchesOf`), queued with
     * the cuts before that same sync.
     *
     * WHY Word's accepted text per range
     *   A range inside an earlier tracked deletion reads as no words once every change
     *   is accepted, so it is skipped the way the paragraph's accepted text skips it,
     *   and a range inside an earlier insertion keeps its words, the way the accepted
     *   text keeps them. The ranges then line up with the words the card was built over.
     *
     * WHY a cell holding a field or a hyperlink is placed by search, not by its ranges
     *   Word for Mac reports a range inside a field or a hyperlink with the field's code
     *   in it (the hyperlink's "HYPERLINK …" between Word's field characters and the
     *   word "Fee"; live hunt, 2026-10-16), so its ranges never read as the paragraph
     *   the card was built over. The reader gives the cell's accepted text with each
     *   field's result, the tokenizer cuts that text into units, and Word's search
     *   finds each unit in the text the document shows.
     *
     * CASES, per cell
     *   - Word's units read as the paragraph the card was built over -> planned
     *   - they do not (`cellEditPlanOf` refuses)                      -> refused alone: the cell is
     *                                                                    not written, the others are,
     *                                                                    and the card reports partly
     *                                                                    applied
     *   - it holds a field or a hyperlink, and Word's search finds
     *     each of its units as often as the reader's text holds it  -> planned over the hits
     *   - it holds a field or a hyperlink, and a unit cannot be
     *     searched for, the counts differ, or Word refused the
     *     searches                                                   -> the engine's package over its
     *                                                                    content range (`packageFallbacks`)
     */
    private static async planWordWrites(context: Word.RequestContext, liveCells: LiveCell[]): Promise<CellWritePlanning> {
        /** WHEN no cell changes CONSEQUENCE nothing is planned and no sync is spent. */
        if (liveCells.length === 0) return { plans: [], refused: [], packageFallbacks: [] };
        const cellsCutByWord = liveCells.filter((liveCell) => !CellChangeWriter.holdsFieldOrHyperlink(liveCell));
        const cellsPlacedBySearch = liveCells.filter((liveCell) => CellChangeWriter.holdsFieldOrHyperlink(liveCell));
        const wordCutUnitsByCell = await CellChangeWriter.readWordCutUnitsOf(context, cellsCutByWord);
        const refused: RefusedCellWrite[] = [];
        const cellEditPlans: CellEditPlan[] = [];
        for (const { liveCell, liveUnits } of wordCutUnitsByCell) {
            try {
                cellEditPlans.push(CellChangeWriter.cellEditPlanOf(liveCell, liveUnits));
            } catch (planRefusal) {
                /**
                 * WHEN Word's units do not read as the paragraph the card was built over
                 *   CONSEQUENCE
                 *     this cell is refused alone and the plan goes on with the others
                 */
                if (!(planRefusal instanceof ApplyFailureError)) throw planRefusal;
                refused.push({ bookmark: liveCell.bookmark, refusal: planRefusal });
            }
        }
        const placedRangeCutsByCell = cellEditPlans.map((cellEditPlan) => CellChangeWriter.queueCutsOfRangesAnEditEndsInside(cellEditPlan));
        const unitSearchesByCell = cellsPlacedBySearch.map((liveCell) => CellChangeWriter.queueUnitSearchesOf(liveCell));
        const searchesWereQueued = placedRangeCutsByCell.some((placedRangeCuts) => placedRangeCuts.length > 0) || unitSearchesByCell.some((unitSearches) => unitSearches !== null);
        /**
         * CASES
         *   - no edit of any cell begins or ends inside a range holding more than its one
         *     unit, and no cell holds a field or a hyperlink whose units can be searched for
         *       -> no search was queued, and the plan needs no third sync
         *   - Word answered the searches -> each cell's ranges cut where the counts line up,
         *       and each searched cell placed where they do
         *   - the sync answering them failed -> no range cut: every edit inside one is
         *       written over the whole range (`searchesAnsweredBy`); every searched cell
         *       takes the package
         */
        const searchesAnswered = searchesWereQueued && (await CellChangeWriter.searchesAnsweredBy(context));
        const plans = cellEditPlans.map((cellEditPlan, cellIndex) =>
            CellChangeWriter.writePlanOf(cellEditPlan, CellChangeWriter.rangesCutBySearches(cellEditPlan.placedRanges, searchesAnswered ? placedRangeCutsByCell[cellIndex] : [])));
        const packageFallbacks: LiveCell[] = [];
        cellsPlacedBySearch.forEach((liveCell, cellIndex) => {
            const unitSearches = unitSearchesByCell[cellIndex];
            const liveUnits = unitSearches === null || !searchesAnswered ? null : CellChangeWriter.unitsPlacedBySearch(unitSearches);
            /** WHEN Word's search could not place the cell's units CONSEQUENCE the engine's package, over the cell's content range. */
            if (liveUnits === null) {
                packageFallbacks.push(liveCell);
                return;
            }
            /** A hit stands on its unit's characters exactly, so no range needs cutting. */
            const cellEditPlan = CellChangeWriter.cellEditPlanOf(liveCell, liveUnits);
            plans.push(CellChangeWriter.writePlanOf(cellEditPlan, cellEditPlan.placedRanges));
        });
        return { plans, refused, packageFallbacks };
    }

    /**
     * Whether Word reports the cell paragraph's ranges with a field code in them: the
     * paragraph holds a complex field, a simple field or a hyperlink, by the engine's
     * one field walk (`OoxmlRange.holdsFieldsOrHyperlinks`).
     */
    private static holdsFieldOrHyperlink(liveCell: LiveCell): boolean {
        return new OoxmlRange(liveCell.liveParagraphOoxml).holdsFieldsOrHyperlinks();
    }

    /**
     * Word's ranges of each cell paragraph, cut at the separators, each with the text
     * Word reads in it once every change is accepted: one sync to cut, one to read.
     *
     * RETURNS
     *   Nothing, without a sync, for no cell.
     */
    private static async readWordCutUnitsOf(context: Word.RequestContext, liveCells: LiveCell[]): Promise<CellWordUnits[]> {
        if (liveCells.length === 0) return [];
        const unitCollections = liveCells.map((liveCell) => ({ liveCell, unitRanges: queueWordRangesOf(liveCell.paragraph.getRange("Content"), WORD_SEPARATORS) }));
        await context.sync();
        const unitReadings = unitCollections.map(({ liveCell, unitRanges }) => ({
            liveCell,
            readings: unitRanges.items.map((unitRange) => ({ unitRange, acceptedText: unitRange.getReviewedText(EVERY_CHANGE_ACCEPTED) })),
        }));
        await context.sync();
        return unitReadings.map(({ liveCell, readings }) => ({ liveCell, liveUnits: readings.map(({ unitRange, acceptedText }) => ({ range: unitRange, acceptedText: acceptedText.value })) }));
    }

    /**
     * The cell paragraph's text with every change accepted, as the engine's reader
     * gives it: each field's result, no field code, struck words left out.
     */
    private static acceptedTextBeforeWriteOf(liveCell: LiveCell): string {
        return new OoxmlRange(liveCell.liveParagraphOoxml).allChangesAcceptedTexts()[0] ?? "";
    }

    /**
     * Queues, on the cell paragraph's content range, one search per distinct text of
     * the diff's units of the cell's accepted text, each unit knowing which occurrence
     * of its text it is; the caller syncs once for every cell's searches.
     *
     * NULL
     *   A unit's text is longer than Word's search takes, or a search would not find
     *   the unit where it stands (an overlap, "ああ" at 1 in "あああ"): nothing is queued,
     *   and the cell takes the package.
     *
     * EXAMPLE
     *   "the fee and the fee"  units the 0..3, fee 4..7, and 8..11, the 12..15, fee 16..19
     *   -> searches "the", "fee", "and"; the second fee is occurrence 1 of 2
     */
    private static queueUnitSearchesOf(liveCell: LiveCell): CellUnitSearches | null {
        const acceptedTextBeforeWrite = CellChangeWriter.acceptedTextBeforeWriteOf(liveCell);
        const unitOccurrences = WordTokenizer.diffUnits(acceptedTextBeforeWrite).map((unitInText) => {
            const occurrenceOffsets = CellChangeWriter.searchHitOffsetsOf(acceptedTextBeforeWrite, unitInText.text);
            return { unitInText, occurrenceOffsets, occurrenceIndex: occurrenceOffsets.indexOf(unitInText.startOffset) };
        });
        const everyUnitIsSearchable = unitOccurrences.every(({ unitInText, occurrenceIndex }) => occurrenceIndex !== -1 && CellChangeWriter.searchTextOf(unitInText.text).length <= WORD_SEARCH_TEXT_MAX_CHARS);
        if (!everyUnitIsSearchable) return null;
        const contentRange = liveCell.paragraph.getRange("Content");
        const searchHitsByText = new Map<string, Word.RangeCollection>();
        const unitSearches = unitOccurrences.map(({ unitInText, occurrenceOffsets, occurrenceIndex }) => {
            const searchHits = searchHitsByText.get(unitInText.text) ?? contentRange.search(CellChangeWriter.searchTextOf(unitInText.text), { matchCase: true });
            /**
             * WHEN this is the first unit of its text
             *   CONSEQUENCE
             *     its search is loaded and kept for every later unit of the same text
             */
            if (!searchHitsByText.has(unitInText.text)) {
                searchHits.load("items");
                searchHitsByText.set(unitInText.text, searchHits);
            }
            return { unitInText, searchHits, expectedHitCount: occurrenceOffsets.length, occurrenceIndex };
        });
        return { liveCell, unitSearches };
    }

    /**
     * The cell's units placed on Word's search hits, one range per unit, each reading
     * as the unit's own text.
     *
     * NULL
     *   Any text was found more or fewer times than the cell's accepted text holds it
     *   (a struck word inside the cell, which a search also reads; Word folding two
     *   spellings of one character together): the hits cannot be told apart, and
     *   writing over the n-th could strike the wrong one.
     */
    private static unitsPlacedBySearch(cellUnitSearches: CellUnitSearches): WordRangeAcceptedReading[] | null {
        const searchesLineUp = cellUnitSearches.unitSearches.every(({ searchHits, expectedHitCount }) => searchHits.items.length === expectedHitCount);
        if (!searchesLineUp) return null;
        return cellUnitSearches.unitSearches.map(({ unitInText, searchHits, occurrenceIndex }) => ({ range: searchHits.items[occurrenceIndex], acceptedText: unitInText.text }));
    }

    /**
     * Syncs the searches queued to cut Word's ranges, and says whether Word answered
     * them.
     *
     * RETURNS
     *   false when the sync failed, and no hit can be read.
     *
     * WHY a failed search is not the card's failure
     *   A search only narrows a write: a range it cannot cut is still written, whole,
     *   as when the hit counts disagree. Word refusing a search costs the narrower
     *   drawing and never the card. The batch held the searches alone,
     *   so nothing else is lost with it.
     */
    private static async searchesAnsweredBy(context: Word.RequestContext): Promise<boolean> {
        try {
            await context.sync();
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Writes the planned changes word by word under Word's own tracking, in one sync for
     * all cells, and journals each cell once the sync answered.
     *
     * WHY one sync for all cells
     *   Word runs a batch in order and keeps every write before the one that fails, so a
     *   sync Word fails part way leaves some of these cells written and the card reports
     *   fewer than it wrote: the document holds the truth, the journal the lower bound.
     */
    private static async writePlannedWords(context: Word.RequestContext, plans: CellWritePlan[], landedWrites: LandedCellWrite[]): Promise<void> {
        /** WHEN no cell is written word by word CONSEQUENCE nothing is journaled and the tracking mode is left alone. */
        if (plans.length === 0) return;
        await withTemporaryTrackingMode(context, TRACK_ALL_CHANGES, async () => {
            for (const plan of plans) CellChangeWriter.queueWrites(plan);
            await context.sync();
        });
        for (const plan of plans) landedWrites.push({ bookmark: plan.liveCell.bookmark, kind: "words" });
    }

    /**
     * Writes the engine's package over each cell paragraph's content range, with Word
     * tracking nothing so the package's own marks are the change, in one sync for all
     * cells, and journals each cell once the sync answered.
     *
     * WHEN a cell takes this door
     *   It holds a field or a hyperlink and Word's search could not place every unit of
     *   its text (`planWordWrites`).
     *
     * WHY the content range
     *   The paragraph's whole range carries the cell's end mark, over which Word for Mac
     *   refuses the package on every cell that ends its row; the content range takes it
     *   on every such cell, in one sync (last-cell probe, 2026-10-16).
     */
    private static async writeContentRangePackages(context: Word.RequestContext, liveCells: LiveCell[], landedWrites: LandedCellWrite[]): Promise<void> {
        /** WHEN no cell takes the package CONSEQUENCE nothing is journaled and the tracking mode is left alone. */
        if (liveCells.length === 0) return;
        await withTemporaryTrackingMode(context, TRACK_NOTHING, async () => {
            for (const liveCell of liveCells) liveCell.paragraph.getRange("Content").insertOoxml(liveCell.built.outcome.packageOoxml, REPLACE_RANGE);
            await context.sync();
        });
        for (const liveCell of liveCells) landedWrites.push({ bookmark: liveCell.bookmark, kind: "package" });
    }

    /**
     * The engine's edits for one cell, and Word's ranges placed on the cell's text:
     * the plan of what to write before any range is cut.
     *
     * WHY the engine's edits of the accepted texts and not the package's marks
     *   The package may remove the user's own earlier insertion outright, as Word does,
     *   with no deletion mark to read; the accepted texts show every word that goes
     *   and every word that comes, whatever the marks. Planned by
     *   `TextEditPlanner.editsBetween`, the edits are the ones the engine wrote into the
     *   package, so this door strikes and inserts what the package door does.
     *
     * WHY the ranges are placed by `AcceptedTextWordRanges.placedOn`
     *   It is the one placement of Word's ranges on a paragraph's accepted text, the one a
     *   comment's quote is placed by too: the unit-for-unit check, the separators Word left
     *   on a range ("(30)" came back as "(30)" plus a non-breaking space, live probe
     *   2026-10-16), and the ranges inside a tracked deletion, placed nowhere. Placed over
     *   the characters Word's range really holds, a unit's end falls inside the range, and
     *   the search cut (`queueCutsOfRangesAnEditEndsInside`) gives the write a range that
     *   ends where the engine's edit does.
     *
     * EDGE CASE
     *   Word's ranges must read, unit for unit, as the paragraph the card was built
     *   over; when they do not (`placedOn` null), this cell is refused (insert_failed)
     *   rather than striking words that were never proposed; the caller writes the
     *   card's other cells.
     *
     * WHY the punctuation cut comes after the placement
     *   It splits a held unit where an edit meets its punctuation
     *   (`heldUnitCutWhereAnEditMeetsItsPunctuation`); the two halves cover the unit's own
     *   characters, so the range's placement stands as `placedOn` gave it.
     */
    private static cellEditPlanOf(liveCell: LiveCell, liveUnits: WordRangeAcceptedReading[]): CellEditPlan {
        const acceptedTextBeforeWrite = CellChangeWriter.acceptedTextBeforeWriteOf(liveCell);
        const acceptedTextAfterWrite = new OoxmlRange(liveCell.built.outcome.packageOoxml).allChangesAcceptedTexts()[0] ?? "";
        const placedOnTheCell = AcceptedTextWordRanges.placedOn(acceptedTextBeforeWrite, liveUnits);
        /** WHEN Word's ranges read other words than the cell CONSEQUENCE the cell is refused, and the card's other cells are written. */
        if (placedOnTheCell === null) throw new ApplyFailureError(CELL_WORDS_DIFFER_MESSAGE, APPLY_FAILURE_INSERT_FAILED);
        const edits = TextEditPlanner.editsBetween(acceptedTextBeforeWrite, acceptedTextAfterWrite);
        const editEdgeOffsets = new Set(edits.flatMap((edit) => [edit.startOffset, edit.endOffset]));
        const placedRanges = placedOnTheCell.map((placedRange): WordRangeOnAcceptedText => ({
            ...placedRange,
            heldUnits: placedRange.heldUnits.flatMap((heldUnit) => CellChangeWriter.heldUnitCutWhereAnEditMeetsItsPunctuation(heldUnit, editEdgeOffsets)),
        }));
        return { liveCell, acceptedTextBeforeWrite, placedRanges, edits };
    }

    /**
     * One held unit, cut in two where its trailing punctuation begins when an engine edit
     * begins or ends exactly there: the word, and the punctuation after it, each a unit a
     * search can place (`queueCutOf`). The cut is the planner's own
     * (`WordTokenizer.splitTrailingPunctuation`), so the edit has an edge where it begins.
     *
     * WHY
     *   Word cuts a cell only at whitespace, and a search cut a range only into whole
     *   diff units, a word with its punctuation. An edit the engine draws between a
     *   word and its "?" widened to the whole unit: "the order?" -> "the order, and
     *   which record shows the signature?" struck "order?" and typed it again
     *   (-26's fix-check §1, every QUESTION cell).
     *
     * CASES
     *   - no punctuation at the unit's end, or the unit is only punctuation -> the unit whole
     *   - no edit begins or ends where its punctuation begins               -> the unit whole: a
     *                                                                          search only narrows a write
     *   - an edit begins or ends there                                      -> the word, then its punctuation
     *
     * EXAMPLE
     *   unit "order?" at 23..29 of the cell, and 0..6 of the range; an edit inserts at 28
     *   -> "order" 23..28 (range 0..5), "?" 28..29 (range 5..6): `startOffset + length of
     *      the word` lands on the "?" in both
     */
    private static heldUnitCutWhereAnEditMeetsItsPunctuation(heldUnit: HeldDiffUnit, editEdgeOffsets: Set<number>): HeldDiffUnit[] {
        const unitInTextSplit = WordTokenizer.splitTrailingPunctuation(heldUnit.unitInText.text);
        const unitInRangeSplit = WordTokenizer.splitTrailingPunctuation(heldUnit.unitInRange.text);
        /** WHEN the unit has no word before punctuation at its end, in the cell or as Word reads it CONSEQUENCE there is nothing to cut it at. */
        if (unitInTextSplit === null || unitInRangeSplit === null || unitInTextSplit.trailingPunctuation.length === 0 || unitInRangeSplit.trailingPunctuation.length === 0) return [heldUnit];
        const punctuationStartInCell = heldUnit.unitInText.startOffset + unitInTextSplit.textBeforeTrailingPunctuation.length;
        /** WHEN no edit begins or ends where the punctuation begins CONSEQUENCE the unit stays whole: no write needs an edge inside it. */
        if (!editEdgeOffsets.has(punctuationStartInCell)) return [heldUnit];
        const punctuationStartInRange = heldUnit.unitInRange.startOffset + unitInRangeSplit.textBeforeTrailingPunctuation.length;
        return [
            {
                unitInText: { text: unitInTextSplit.textBeforeTrailingPunctuation, startOffset: heldUnit.unitInText.startOffset, endOffset: punctuationStartInCell },
                unitInRange: { text: unitInRangeSplit.textBeforeTrailingPunctuation, startOffset: heldUnit.unitInRange.startOffset, endOffset: punctuationStartInRange },
            },
            {
                unitInText: { text: unitInTextSplit.trailingPunctuation, startOffset: punctuationStartInCell, endOffset: heldUnit.unitInText.endOffset },
                unitInRange: { text: unitInRangeSplit.trailingPunctuation, startOffset: punctuationStartInRange, endOffset: heldUnit.unitInRange.endOffset },
            },
        ];
    }

    /**
     * Queues the searches that cut each placed range an edit begins or ends inside
     * into the diff's units it holds, so the edit can start or stop between two of
     * them, or at a unit's end that Word's range reaches past: Word cuts a cell only
     * at whitespace, and "本製品の保証期間は一年とする。" is one Word range, where the
     * engine strikes "製品" alone; "(30)" with a non-breaking space after it is one Word range, where the engine
     * strikes "(30)" and keeps the non-breaking space.
     *
     * CASES, per placed range
     *   - it holds exactly its one unit, or no edit begins or ends strictly inside it -> not searched
     *   - a unit's text is longer than Word searches, or the unit is not where a
     *     search finds that text (an overlap, "ああ" at 1 in "あああ")           -> not searched: written whole
     *   - otherwise -> one search per distinct text of its units, queued now and
     *     answered by the caller's one sync
     *
     * RETURNS
     *   The cuts queued; the caller syncs once for all of them.
     */
    private static queueCutsOfRangesAnEditEndsInside(cellEditPlan: CellEditPlan): PlacedRangeCut[] {
        const editEdgeOffsets = cellEditPlan.edits.flatMap((edit) => [edit.startOffset, edit.endOffset]);
        const rangesAnEditEndsInside = cellEditPlan.placedRanges.filter((placedRange) =>
            CellChangeWriter.holdsMoreThanItsOneUnit(placedRange) && editEdgeOffsets.some((editEdgeOffset) => placedRange.startOffset < editEdgeOffset && editEdgeOffset < placedRange.endOffset));
        return rangesAnEditEndsInside.flatMap((placedRange) => {
            const placedRangeCut = CellChangeWriter.queueCutOf(placedRange);
            return placedRangeCut === null ? [] : [placedRangeCut];
        });
    }

    /**
     * Whether a placed range holds more than the one unit a search could cut it down
     * to: several units, or one unit with the separators Word left on the range.
     *
     * EXAMPLE
     *   "本製品の保証期間は一年とする。" (9 units)       -> true
     *   "(30)" plus a non-breaking space, placed 7..12, its unit "(30)" 7..11 -> true
     *   "(30)" placed 7..11, its unit 7..11              -> false
     */
    private static holdsMoreThanItsOneUnit(placedRange: WordRangeOnAcceptedText): boolean {
        if (placedRange.heldUnits.length > 1) return true;
        const onlyUnit = placedRange.heldUnits[0].unitInText;
        return placedRange.startOffset < onlyUnit.startOffset || onlyUnit.endOffset < placedRange.endOffset;
    }

    /**
     * The searches that cut one placed range into its held units, one per distinct
     * text, each held unit knowing which occurrence of its text it is.
     *
     * NULL
     *   A held unit's text is longer than Word's search takes, or a search would not
     *   find the unit where it stands; nothing is queued and the range stays whole.
     *
     * EXAMPLE
     *   range text "本製品の製品"  held units 本 0..1, 製品 1..3, の 3..4, 製品 4..6
     *   -> searches "本", "製品", "の"; the second 製品 is occurrence 1 of 2
     */
    private static queueCutOf(placedRange: WordRangeOnAcceptedText): PlacedRangeCut | null {
        const searchableHeldUnits = placedRange.heldUnits.map((heldUnit) => {
            const occurrenceOffsets = CellChangeWriter.searchHitOffsetsOf(placedRange.acceptedTextInWord, heldUnit.unitInRange.text);
            return { heldUnit, occurrenceOffsets, occurrenceIndex: occurrenceOffsets.indexOf(heldUnit.unitInRange.startOffset) };
        });
        /**
         * WHEN any held unit cannot be found by a search where it stands
         *   CONSEQUENCE
         *     the range is not cut: a write that ends inside it takes the whole range
         */
        const everyHeldUnitIsSearchable = searchableHeldUnits.every(({ heldUnit, occurrenceIndex }) => occurrenceIndex !== -1 && CellChangeWriter.searchTextOf(heldUnit.unitInRange.text).length <= WORD_SEARCH_TEXT_MAX_CHARS);
        if (!everyHeldUnitIsSearchable) return null;
        const searchHitsByText = new Map<string, Word.RangeCollection>();
        const heldUnitSearches = searchableHeldUnits.map(({ heldUnit, occurrenceOffsets, occurrenceIndex }) => {
            const unitText = heldUnit.unitInRange.text;
            const searchHits = searchHitsByText.get(unitText) ?? placedRange.range.search(CellChangeWriter.searchTextOf(unitText), { matchCase: true });
            /**
             * WHEN this is the first held unit of its text
             *   CONSEQUENCE
             *     its search is loaded and kept for every later unit of the same text
             */
            if (!searchHitsByText.has(unitText)) {
                searchHits.load("items");
                searchHitsByText.set(unitText, searchHits);
            }
            return { heldUnit, searchHits, expectedHitCount: occurrenceOffsets.length, occurrenceIndex };
        });
        return { placedRange, heldUnitSearches };
    }

    /**
     * Where Word's search finds `searchedText` in `rangeText`: every occurrence from
     * the left, each search going on after the end of the one before, so two never
     * overlap.
     *
     * EXAMPLE
     *   rangeText "本製品の製品", searchedText "製品" -> [1, 4]
     *   rangeText "あああ", searchedText "ああ"       -> [0]: the next search starts at 0 + 2 = 2
     */
    private static searchHitOffsetsOf(rangeText: string, searchedText: string): number[] {
        const searchHitOffsets: number[] = [];
        /**
         * EXAMPLE
         *   hit at 1, searchedText "製品" (length 2) -> the next search starts at 1 + 2 = 3
         */
        for (let hitOffset = rangeText.indexOf(searchedText); hitOffset !== -1; hitOffset = rangeText.indexOf(searchedText, hitOffset + searchedText.length)) searchHitOffsets.push(hitOffset);
        return searchHitOffsets;
    }

    /**
     * The text Word's search is asked for to find `unitText` as written.
     *
     * WHY the caret is doubled
     *   Word reads "^" as the start of a special code even without wildcards ("^t" a
     *   tab, "^p" a paragraph mark, "^13" a carriage return); "^^" is a literal caret.
     *
     * EXAMPLE
     *   "2^10" -> "2^^10"
     */
    private static searchTextOf(unitText: string): string {
        return unitText.replace(/\^/g, "^^");
    }

    /**
     * The placed ranges with every range whose searches line up replaced by one range
     * per held unit, each a search hit placed on that unit's characters.
     *
     * CASES, per range searched
     *   - every text was found exactly as often as the range's text holds it -> cut
     *   - any count differs (a struck word inside the range, which a search also
     *     reads; Word folding two spellings of one character together)       -> whole,
     *     as if never searched: the hits cannot be told apart, and writing over the
     *     n-th could strike the wrong one
     */
    private static rangesCutBySearches(placedRanges: WordRangeOnAcceptedText[], placedRangeCuts: PlacedRangeCut[]): WordRangeOnAcceptedText[] {
        return placedRanges.flatMap((placedRange) => {
            const placedRangeCut = placedRangeCuts.find((candidateCut) => candidateCut.placedRange === placedRange);
            const searchesLineUp = placedRangeCut !== undefined && placedRangeCut.heldUnitSearches.every(({ searchHits, expectedHitCount }) => searchHits.items.length === expectedHitCount);
            if (!searchesLineUp) return [placedRange];
            return placedRangeCut.heldUnitSearches.map(({ heldUnit, searchHits, occurrenceIndex }) => ({
                range: searchHits.items[occurrenceIndex],
                startOffset: heldUnit.unitInText.startOffset,
                endOffset: heldUnit.unitInText.endOffset,
                acceptedTextInWord: heldUnit.unitInRange.text,
                heldUnits: [{ unitInText: heldUnit.unitInText, unitInRange: { text: heldUnit.unitInRange.text, startOffset: 0, endOffset: heldUnit.unitInRange.text.length } }],
            }));
        });
    }

    /**
     * The writes one cell needs: each engine edit laid on Word's ranges, widened
     * where it begins or ends somewhere Word gives no range edge, and grouped with
     * the edits before it where their stretches then overlap or touch.
     *
     * CASES, per edit edge
     *   - at a range's start or end                       -> that edge, the engine's own cut
     *   - where a word's trailing punctuation begins      -> the edge between the word and its
     *                                                        punctuation, a search having cut them
     *                                                        apart (`heldUnitCutWhereAnEditMeetsItsPunctuation`)
     *   - inside a range a search could not cut           -> the range's start or end: the
     *                                                        write takes its characters again
     *   - inside the spacing between two ranges           -> the edge of the range on that side:
     *                                                        the write takes the spacing again
     *   - at a unit's end inside a range Word left a
     *     separator on, cut by a search                   -> the hit's end: the separator stays
     *                                                        (`cellEditPlanOf`)
     *
     * WHY grouped when they overlap or touch
     *   Two edits widened over one uncut range would each write the whole range, the
     *   second undoing the first; two writes meeting at one edge would both write
     *   through it in one sync. Grouped, the stretch is written once with every edit
     *   in it.
     */
    private static writePlanOf(cellEditPlan: CellEditPlan, placedRanges: WordRangeOnAcceptedText[]): CellWritePlan {
        const { liveCell, acceptedTextBeforeWrite, edits } = cellEditPlan;
        /**
         * WHEN Word reads no word in the cell
         *   CONSEQUENCE
         *     there is no range to write at: the card's text goes at the paragraph's start
         */
        if (placedRanges.length === 0) return { liveCell, rangeWrites: [], textForEmptyCell: edits.map((edit) => edit.replacementText).join("") };
        const editGroups: CellEditGroup[] = [];
        for (const edit of edits) {
            const editEdges = CellChangeWriter.edgesOf(edit, placedRanges);
            const lastEditGroup = editGroups[editGroups.length - 1];
            /**
             * WHEN this edit's stretch begins at or before the end of the group before it
             *   CONSEQUENCE
             *     it joins that group, which reaches to the further of the two ends
             *
             * EXAMPLE
             *   uncut range 0..15, edits 1..3 and 9..11 both widened to 0..15 -> one group 0..15
             */
            if (lastEditGroup !== undefined && editEdges.startEdge.offset <= lastEditGroup.endEdge.offset) {
                lastEditGroup.edits.push(edit);
                if (editEdges.endEdge.offset > lastEditGroup.endEdge.offset) lastEditGroup.endEdge = editEdges.endEdge;
                continue;
            }
            editGroups.push({ ...editEdges, edits: [edit] });
        }
        return {
            liveCell,
            rangeWrites: editGroups.map((editGroup) => ({ startEdge: editGroup.startEdge, endEdge: editGroup.endEdge, writtenText: CellChangeWriter.textOfEditGroup(editGroup, acceptedTextBeforeWrite) })),
            textForEmptyCell: "",
        };
    }

    /**
     * The range edges one engine edit is written between: its own two ends when they
     * stand on edges, else the nearest edges outside them.
     *
     * CASES, for an insertion (the edit's two ends at one offset)
     *   - a range starts there -> a point before that range: the engine's offset is the
     *                             place in front of the next unit
     *   - a range ends there   -> a point after that range
     *   - otherwise            -> the nearest edges either side, as a replacement
     *
     * EXAMPLE
     *   cell "本製品の保証期間は一年とする。" as one uncut range 0..15, edit 1..3 "商品"
     *   -> the range's start (0) and end (15)
     */
    private static edgesOf(edit: PlannedTextEdit, placedRanges: WordRangeOnAcceptedText[]): PlacedRangeEdges {
        const editIsInsertion = edit.startOffset === edit.endOffset;
        const rangeStartingAtEdit = placedRanges.find((placedRange) => placedRange.startOffset === edit.startOffset);
        const rangeEndingAtEdit = placedRanges.find((placedRange) => placedRange.endOffset === edit.startOffset);
        if (editIsInsertion && rangeStartingAtEdit !== undefined) {
            const pointBeforeRange: PlacedRangeEdge = { placedRange: rangeStartingAtEdit, side: "Start", offset: edit.startOffset };
            return { startEdge: pointBeforeRange, endEdge: pointBeforeRange };
        }
        if (editIsInsertion && rangeEndingAtEdit !== undefined) {
            const pointAfterRange: PlacedRangeEdge = { placedRange: rangeEndingAtEdit, side: "End", offset: edit.startOffset };
            return { startEdge: pointAfterRange, endEdge: pointAfterRange };
        }
        return { startEdge: CellChangeWriter.edgeAtOrBefore(edit.startOffset, placedRanges), endEdge: CellChangeWriter.edgeAtOrAfter(edit.endOffset, placedRanges) };
    }

    /**
     * The text one group writes between its edges: the cell's own characters from the
     * start edge to the first edit, each edit's text, the cell's characters between
     * one edit and the next, and the cell's characters from the last edit to the end
     * edge.
     *
     * EXAMPLE
     *   group 0..15 over "本製品の保証期間は一年とする。", edits 1..3 "商品" and 9..11 "二年"
     *   -> "本" + "商品" + "の保証期間は" + "二年" + "とする。"
     */
    private static textOfEditGroup(editGroup: CellEditGroup, acceptedTextBeforeWrite: string): string {
        let writtenText = "";
        let cellTextCopiedUpTo = editGroup.startEdge.offset;
        for (const edit of editGroup.edits) {
            /**
             * EXAMPLE
             *   copied up to 3, edit at 9..11 -> the cell's own 3..9 "の保証期間は", then "二年"; copied up to 11
             */
            writtenText += `${acceptedTextBeforeWrite.slice(cellTextCopiedUpTo, edit.startOffset)}${edit.replacementText}`;
            cellTextCopiedUpTo = edit.endOffset;
        }
        return `${writtenText}${acceptedTextBeforeWrite.slice(cellTextCopiedUpTo, editGroup.endEdge.offset)}`;
    }

    /**
     * The nearest range edge at or before `offset`, where a write that must begin
     * at `offset` can begin instead.
     *
     * CASES, for the last range starting at or before `offset`
     *   - it starts at `offset`                -> its start
     *   - it ends at or before `offset`        -> its end: `offset` is in the spacing after it
     *   - `offset` falls inside it             -> its start
     *
     * EDGE CASE
     *   No range starts at or before `offset`: the engine never edits the spacing in
     *   front of a cell's first word, so this is the first range's start.
     */
    private static edgeAtOrBefore(offset: number, placedRanges: WordRangeOnAcceptedText[]): PlacedRangeEdge {
        const rangesStartingAtOrBefore = placedRanges.filter((placedRange) => placedRange.startOffset <= offset);
        /**
         * EXAMPLE
         *   3 ranges start at or before the offset -> the last is index 3 - 1 = 2
         */
        const lastRangeStartingAtOrBefore = rangesStartingAtOrBefore[rangesStartingAtOrBefore.length - 1];
        if (lastRangeStartingAtOrBefore === undefined) return { placedRange: placedRanges[0], side: "Start", offset: placedRanges[0].startOffset };
        if (lastRangeStartingAtOrBefore.startOffset < offset && lastRangeStartingAtOrBefore.endOffset <= offset) return { placedRange: lastRangeStartingAtOrBefore, side: "End", offset: lastRangeStartingAtOrBefore.endOffset };
        return { placedRange: lastRangeStartingAtOrBefore, side: "Start", offset: lastRangeStartingAtOrBefore.startOffset };
    }

    /**
     * The nearest range edge at or after `offset`, where a write that must end at
     * `offset` can end instead.
     *
     * CASES, for the first range ending at or after `offset`
     *   - it ends at `offset`                  -> its end
     *   - it starts at or after `offset`       -> its start: `offset` is in the spacing before it
     *   - `offset` falls inside it             -> its end
     *
     * EDGE CASE
     *   No range ends at or after `offset`: the engine never edits the spacing after a
     *   cell's last word, so this is the last range's end.
     */
    private static edgeAtOrAfter(offset: number, placedRanges: WordRangeOnAcceptedText[]): PlacedRangeEdge {
        const firstRangeEndingAtOrAfter = placedRanges.find((placedRange) => placedRange.endOffset >= offset);
        if (firstRangeEndingAtOrAfter === undefined) {
            /**
             * EXAMPLE
             *   4 ranges -> the last is index 4 - 1 = 3
             */
            const lastRange = placedRanges[placedRanges.length - 1];
            return { placedRange: lastRange, side: "End", offset: lastRange.endOffset };
        }
        if (firstRangeEndingAtOrAfter.endOffset > offset && firstRangeEndingAtOrAfter.startOffset >= offset) return { placedRange: firstRangeEndingAtOrAfter, side: "Start", offset: firstRangeEndingAtOrAfter.startOffset };
        return { placedRange: firstRangeEndingAtOrAfter, side: "End", offset: firstRangeEndingAtOrAfter.endOffset };
    }

    /**
     * Queues every write of one cell on its Word ranges; the caller syncs under
     * Word's tracking, so Word strikes and inserts the characters itself.
     *
     * CASES, per write
     *   - a point at a range's start          -> its text inserted before the range
     *   - a point at a range's end            -> its text inserted after the range
     *   - one range from its start to its end -> that range written over
     *   - any other two edges                 -> the stretch from one edge to the other: a
     *                                            range's end or start where the write begins
     *                                            or stops in the spacing between ranges
     *   - written text ""                     -> the stretch deleted
     *   - a cell Word reads no word in        -> the card's text at the paragraph's start
     */
    private static queueWrites(plan: CellWritePlan): void {
        /**
         * WHEN the cell has no word for a range to stand on and the card writes into it
         *   CONSEQUENCE
         *     its text goes at the paragraph's start
         */
        if (plan.textForEmptyCell.length > 0) plan.liveCell.paragraph.insertText(CellChangeWriter.asWordText(plan.textForEmptyCell), "Start");
        for (const { startEdge, endEdge, writtenText } of plan.rangeWrites) {
            const writtenWordText = CellChangeWriter.asWordText(writtenText);
            const writeIsPoint = startEdge.offset === endEdge.offset;
            /**
             * WHEN the write is a point
             *   CONSEQUENCE
             *     inserted on the side of the range its edge names, nothing struck
             */
            if (writeIsPoint) {
                startEdge.placedRange.range.insertText(writtenWordText, startEdge.side === "Start" ? "Before" : "After");
                continue;
            }
            const writtenRange = CellChangeWriter.rangeBetween(startEdge, endEdge);
            /**
             * CASES
             *   - nothing is written in the stretch's place -> it is deleted, and nothing is inserted
             *   - otherwise                                -> it is written over
             */
            if (writtenWordText.length === 0) writtenRange.delete();
            else writtenRange.insertText(writtenWordText, REPLACE_RANGE);
        }
    }

    /**
     * The Word range from one edge to another.
     *
     * CASES
     *   - the start and the end of one range -> that range itself
     *   - otherwise                          -> grown from the start edge to the end edge, where a
     *                                           range's start is the range and a range's end is its
     *                                           "End" point, and the other way round at the end
     *
     * EXAMPLE
     *   "the" End (10) to "subscription" Start (11) -> the space between them
     */
    private static rangeBetween(startEdge: PlacedRangeEdge, endEdge: PlacedRangeEdge): Word.Range {
        const edgesSpanOneRange = startEdge.placedRange === endEdge.placedRange && startEdge.side === "Start" && endEdge.side === "End";
        if (edgesSpanOneRange) return startEdge.placedRange.range;
        const rangeFromStart = startEdge.side === "Start" ? startEdge.placedRange.range : startEdge.placedRange.range.getRange("End");
        const rangeToEnd = endEdge.side === "End" ? endEdge.placedRange.range : endEdge.placedRange.range.getRange("Start");
        return rangeFromStart.expandTo(rangeToEnd);
    }

    /**
     * The text as Word's `insertText` writes it: the card's characters, with each line
     * break as the character Word writes a manual line break for.
     *
     * WHY
     *   The cell's text reads a manual line break (`w:br`) as "\n"; given "\n", Word's
     *   insertText starts a new paragraph instead, which is not the card's line break.
     *   A tab, a non-breaking space and every other character go in as they are.
     *
     * EXAMPLE
     *   "15% per\nannum" -> "15% per\u000bannum"
     */
    private static asWordText(cellText: string): string {
        return cellText.replace(/\n/g, WORD_MANUAL_LINE_BREAK);
    }

    /**
     * Lays each written cell's bookmark back over the paragraph Word holds now: one
     * sync to read the paragraphs' ids and texts, then one lay-over per cell.
     */
    private static async layBookmarksBack(context: Word.RequestContext, writtenParagraphs: WrittenCellParagraph[], bookmarks: CellBookmarkRelayer): Promise<void> {
        /** WHEN no bookmark is to be laid CONSEQUENCE no sync is spent. */
        if (writtenParagraphs.length === 0) return;
        for (const { paragraph } of writtenParagraphs) paragraph.load("uniqueLocalId,text");
        await context.sync();
        for (const { bookmark, paragraph } of writtenParagraphs) await bookmarks.layOver([bookmark], [{ uniqueLocalId: paragraph.uniqueLocalId, text: paragraph.text }]);
    }

    /**
     * Lays the written cells' bookmarks back on the way out of a failed write, where the
     * failure being reported matters more than a lay-over Word refuses.
     *
     * FALLBACK
     *   A refused lay-over is sent to Sentry and the failure that brought the card here is
     *   still the one reported.
     */
    private static async layBookmarksBackQuietly(
        context: Word.RequestContext,
        writtenParagraphs: WrittenCellParagraph[],
        bookmarks: CellBookmarkRelayer,
        cardId: string | null,
    ): Promise<void> {
        try {
            await CellChangeWriter.layBookmarksBack(context, writtenParagraphs, bookmarks);
        } catch (layOverFailure) {
            reportError(layOverFailure, {
                tags: { where: "cell-change-writer.lay-bookmarks-back" },
                extra: { cardId, bookmarks: writtenParagraphs.map((writtenParagraph) => writtenParagraph.bookmark), wordError: wordErrorDetailOf(layOverFailure) },
            });
        }
    }
}
